import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import LoadingScreen from '../../components/LoadingScreen'
import TabsMui from '../../layout/TabsMui'
import AlertErrorMessage from '../price_list/helpers/AlertErrorMessage'
import { canManageSpecialPrices } from '../../global_context/capabilities/priceCapabilities'
import SpecialPriceDetailSection from './components/SpecialPriceDetailSection'
import SpecialPriceProductTable from './components/SpecialPriceProductTable'
import SpecialPriceScaleList from './components/SpecialPriceScaleList'
import SpecialPriceStatusBadge from './components/SpecialPriceStatusBadge'

import { fetchSpecialPriceDetail, suspendSpecialPrice } from './helpers/specialPriceApi'
import {
  SPECIAL_PRICE_DETAIL_TABS,
  SPECIAL_PRICE_DETAIL_TAB_VALUE,
  SPECIAL_PRICE_SHOW_FETCH_ERROR_MESSAGE,
  SPECIAL_PRICE_SHOW_NOT_FOUND_MESSAGE,
  SPECIAL_PRICE_SHOW_SUSPEND_CONFIRMATION,
  SPECIAL_PRICE_SHOW_SUSPEND_ERROR_MESSAGE,
  SPECIAL_PRICE_SHOW_TITLE
} from './constant/specialPriceViewConstants'
import {
  extractErrorMessage
} from './helpers/specialPriceHelpers'
import { buildSpecialPriceHeaderActions } from './helpers/specialPriceShowHelpers'
import '../../css/special_price.scss'

const ShowSpecialPriceView = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState(SPECIAL_PRICE_DETAIL_TAB_VALUE)
  const [isFetching, setIsFetching] = useState(true)
  const [details, setDetails] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const canWrite = canManageSpecialPrices()

  const fetchDetails = useCallback(async () => {
    setIsFetching(true)
    setErrorMessage('')

    try {
      const data = await fetchSpecialPriceDetail(id)
      setDetails(data)
    } catch (error) {
      console.error(error)
      setErrorMessage(SPECIAL_PRICE_SHOW_FETCH_ERROR_MESSAGE)
    } finally {
      setIsFetching(false)
    }
  }, [id])

  useEffect(() => {
    fetchDetails()
  }, [fetchDetails])

  const handleSuspend = useCallback(async () => {
    const confirmed = window.confirm(SPECIAL_PRICE_SHOW_SUSPEND_CONFIRMATION)

    if (!confirmed) return

    try {
      const response = await suspendSpecialPrice(id)

      if (!response?.ok && response?.status !== 204) {
        throw new Error(
          extractErrorMessage(response?.data, SPECIAL_PRICE_SHOW_SUSPEND_ERROR_MESSAGE)
        )
      }

      fetchDetails()
    } catch (error) {
      setErrorMessage(error.message || SPECIAL_PRICE_SHOW_SUSPEND_ERROR_MESSAGE)
    }
  }, [fetchDetails, id])

  const handleCopy = useCallback(() => {
    navigate(`/crear_precio_especial?copy=${id}`)
  }, [id, navigate])

  const headerActions = useMemo(
    () =>
      canWrite && details
        ? buildSpecialPriceHeaderActions({
            status: details.status,
            onSuspend: handleSuspend,
            onCopy: handleCopy
          })
        : [],
    [canWrite, details, handleCopy, handleSuspend]
  )

  if (isFetching) {
    return <LoadingScreen text="Cargando..." />
  }

  if (!details) {
    return (
      <AlertErrorMessage
        message={errorMessage || SPECIAL_PRICE_SHOW_NOT_FOUND_MESSAGE}
        onClose={() => setErrorMessage('')}
      />
    )
  }

  return (
    <>
      {errorMessage && (
        <AlertErrorMessage message={errorMessage} onClose={() => setErrorMessage('')} />
      )}

      <div className="main-show-header">
        <h2>{SPECIAL_PRICE_SHOW_TITLE}</h2>
      </div>

      <div className="index-table">
        <div className="show-area">
          <div className="create-promo-wrapper special-price-show-wrapper">
            <div className="special-price-show-topbar">
              <TabsMui
                tabs={SPECIAL_PRICE_DETAIL_TABS}
                activeTab={activeTab}
                onChange={setActiveTab}
              />

              <div className="special-price-show-status">
                <span className="special-price-show-status-label">Estado:</span>
                <SpecialPriceStatusBadge status={details.status} />
              </div>
            </div>

            {activeTab === SPECIAL_PRICE_DETAIL_TAB_VALUE && (
              <>
                <SpecialPriceDetailSection details={details} actions={headerActions} />

                <div className="task-form-section">
                  <hr className="mb-10" />

                  <SpecialPriceProductTable details={details} />
                </div>

                <SpecialPriceScaleList details={details} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ShowSpecialPriceView
